'use client';

import Link from 'next/link';

interface StoreErrorViewProps {
	reset?: () => void;
	eyebrow?: string;
	title?: string;
	description?: string;
}

/**
 * Shared branded error screen used by the storefront error boundaries
 * (route-level `error.tsx` for page errors, and the parent boundary that
 * catches a CMS-unavailable throw from the market layout).
 */
export function StoreErrorView({
	reset,
	eyebrow = 'Something Went Wrong',
	title = 'We Hit a Snag',
	description = 'Something went wrong loading this page. Try again, or head back to keep shopping.',
}: StoreErrorViewProps) {
	return (
		<section className="relative flex min-h-[60vh] flex-1 flex-col overflow-hidden bg-neutral-950">
			{/* Ghosted background text — matches Dead End hero treatment */}
			<span
				aria-hidden="true"
				className="pointer-events-none absolute inset-0 flex select-none items-center justify-center text-[8rem] font-black leading-none text-white/[0.03] sm:text-[14rem] md:text-[18rem] xl:text-[22rem]"
			>
				ERROR
			</span>

			{/* Gradient overlay matching hero */}
			<div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/24 to-black/14" />

			{/* Content — bottom-left aligned like the hero and 404 */}
			<div className="container-page relative z-10 flex flex-1 items-end pb-8 pt-16 sm:pb-12 sm:pt-24 lg:pb-16 lg:pt-28">
				<div className="flex max-w-3xl flex-col items-start gap-0 text-left">
					<p className="mb-3 font-semibold uppercase text-[11px] tracking-[0.3em] text-white/70 sm:text-xs">
						{eyebrow}
					</p>

					<h1 className="font-black uppercase leading-[0.95] tracking-tight text-4xl text-white sm:text-6xl lg:text-7xl xl:text-8xl">
						{title}
					</h1>

					<p className="mt-3 max-w-xl text-base text-white/80 sm:text-lg">{description}</p>

					<div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 justify-start sm:mt-8 sm:gap-4">
						{reset ? (
							<button
								type="button"
								onClick={reset}
								className="inline-flex items-center justify-center rounded bg-white px-6 py-3 text-xs font-bold uppercase tracking-wider text-neutral-900 transition-colors hover:bg-neutral-200 sm:px-8 sm:py-3.5 sm:text-sm"
							>
								Try Again
							</button>
						) : null}
						<Link
							href="/"
							className="inline-flex items-center justify-center rounded border border-white/30 px-6 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-white/10 sm:px-8 sm:py-3.5 sm:text-sm"
						>
							Back to Home
						</Link>
					</div>
				</div>
			</div>
		</section>
	);
}
